import React, { Component } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import SectionLabel from 'components/controls/sectionLabel';
import { DESCRIPTION_ROWS, TITLE_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import { GoX } from 'react-icons/go';
import Select from 'components/controls/select';
import StatusPill from '../../components/statusPill';
import { workflowAlias, workflowSelection } from '../../domain/cellWorkflow';
import DetailsView from './detailsView';
import WorkflowGraph from './workflowGraph';
import { STATUSES } from '../../domain/schema';
import { upsertMission } from '../../domain/model';

// Leading "clear" option for the workspace / cell-workflow name pickers (value '' → save clears to null).
const NONE_OPTION = { value: '', label: '— none —' };

/**
 * MissionPanel — detail panel shown in the right dock when no cell or dependency is selected. It is
 * the resting state of the detail panel; its × collapses the whole detail dock.
 *
 * Layout: a single "Mission" section (`Card.Text as="h6"` + `<hr/>`) with three SectionLabel
 * subsections — Status (the per-cell-status counts), Details (the editable Title + Description), and
 * Workspace (the mission-level default workspace picker). All values read from the store. It mirrors
 * CellPanel's structure and classes (h6 section header, the platform's gray-chip `.properties` /
 * `.prop-name-column` table) so it renders identically with no panel-specific CSS. (Mission switching
 * lives in the widget header, not here — see missions/index.js.)
 *
 * The mission's Title and Description are editable (missions have no draft gate, so ALWAYS editable).
 * Each field saves on blur via client.setMissionProps — which merges the change into the mission graph
 * object's OWN metadata, non-destructively to its cells — then reflects it locally via
 * onApplyLocal(upsertMission) so the panel updates in place with no reload. The write is skipped when
 * the value is unchanged.
 *
 * The Workspace subsection is the mission's OWN default workspace ({ type, work_folder } | null, stored
 * at graph.metadata.workspace — flattened onto the mission object as mission.workspace by
 * client.listMissions()), which a root cell's workspace_mode "inherit_from_mission" copies (see
 * cellPanel.js). It mirrors CellPanel's specify_workspace picker: a <Select> over
 * client.getTemplates().workspaces (by name), writing the chosen entry's { type, work_folder } as an
 * embedded snapshot — never a reference by name (workspaces are ACTUAL, not templates — schema.js) —
 * via client.setMissionProps(missionId, { workspace }). The current selection is reverse-matched by
 * work_folder since the embedded copy doesn't retain the palette entry's name. Saves immediately on
 * pick (no draft/blur step, like the mission title/description).
 *
 * Props:
 *   mission      — mission object { id, title?, description?, workspace? } or null
 *   missionId    — string (the active mission id)
 *   cells        — array of cell vertex objects for the mission
 *   client       — MissionsClient instance
 *   onApplyLocal — (updater: store => store) => void — incremental local store update
 *   onClose      — () => void  — collapse the detail dock
 *   templates    — store.templates ({ workflows, workspaces } palettes)
 */
class MissionPanel extends Component {
  constructor(props) {
    super(props);
    const mission = props.mission || {};
    this.state = {
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
    };
  }

  componentDidUpdate(prevProps) {
    // Re-seed the editable drafts only when the selected mission's identity changes — never on a plain
    // store update (which would clobber an in-progress edit).
    if ((prevProps.mission && prevProps.mission.id) !== (this.props.mission && this.props.mission.id)) {
      this._seedDrafts();
    }
  }

  // Seed the editable title/description drafts from the current mission.
  _seedDrafts() {
    const mission = this.props.mission || {};
    this.setState({
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
    });
  }

  // Merge a metadata change into the store locally (no reload) so the switcher + panel reflect it.
  _applyLocal(changed) {
    const { mission, onApplyLocal } = this.props;
    if (!onApplyLocal || !mission) return;
    onApplyLocal(s => upsertMission(s, { ...mission, ...changed }));
  }

  async _saveTitle() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.titleDraft;
    const current = mission.title || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { title: next });
      this._applyLocal({ title: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveDescription() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.descriptionDraft;
    const current = mission.description || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { description: next });
      this._applyLocal({ description: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Workspace-name Select onChange (mission-level default). Copies the chosen palette entry's
  // { type, work_folder } as an embedded snapshot — never a reference by name (see schema.js:
  // workspaces are ACTUAL, not templates) — mirroring CellPanel's _saveWorkspace. Saves immediately
  // (no draft step) since the Select's own onChange already carries the picked value.
  async _saveWorkspace(alias) {
    const { mission, missionId, client, templates } = this.props;
    if (!mission || !client) return;
    const workspaces = (templates && templates.workspaces) || [];
    const currentWorkspace = mission.workspace || null;
    const currentEntry = currentWorkspace
      ? workspaces.find(w => w.work_folder === currentWorkspace.work_folder)
      : null;
    const currentAlias = (currentEntry && currentEntry.alias) || '';
    const selected = alias || '';
    if (selected === currentAlias) return;
    const entry = selected ? workspaces.find(w => w.alias === selected) : null;
    const workspace = entry ? { type: entry.type, work_folder: entry.work_folder } : null;
    try {
      await client.setMissionProps(missionId, { workspace });
      this._applyLocal({ workspace });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Cell-workflow Select onChange (mission-level default). Writes the chosen workflow BODY (or null to
  // clear) via client.setMissionProps; a root cell whose cell_workflow_mode is "inherit_from_mission"
  // resolves to this at runtime. Saves immediately on pick (like the title/description/workspace).
  async _saveCellWorkflow(alias) {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const current = workflowAlias(mission.cell_workflow);
    const selected = alias || '';
    if (selected === current) return;
    const next = selected || null;
    try {
      const body = workflowSelection(next, (this.props.templates || {}).workflows || []);
      await client.setMissionProps(missionId, { cell_workflow: body });
      this._applyLocal({ cell_workflow: body });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  render() {
    const { mission, missionId, cells, onClose, templates } = this.props;
    // A mission has no draft gate of its own — the only thing that makes this panel read-only is a
    // committed revision, a closed session or no write access. Default true so an embedding that
    // does not express the seam behaves as it did.
    const canEdit = this.props.canEdit !== false;
    const { titleDraft, descriptionDraft } = this.state;

    // Mission-level default workspace picker (mirrors CellPanel's specify_workspace picker). The
    // current selection is reverse-matched by work_folder since the embedded copy on the mission
    // doesn't retain the palette entry's alias.
    const missionWorkspace = (mission && mission.workspace) || null;
    const workspaceTemplates = (templates && templates.workspaces) || [];
    const workspaceOptions = [
      NONE_OPTION,
      ...(Array.isArray(workspaceTemplates)
        ? workspaceTemplates.map(w => w.alias).filter(Boolean).map(a => ({ value: a, label: a }))
        : []),
    ];
    const selectedWorkspaceEntry = missionWorkspace
      ? workspaceTemplates.find(w => w.work_folder === missionWorkspace.work_folder) || null
      : null;
    const workspaceSelectValue = (selectedWorkspaceEntry
      && workspaceOptions.find(o => o.value === selectedWorkspaceEntry.alias)) || NONE_OPTION;

    // Mission-level default cell workflow (workflows is a LIST of { alias, ... }); root cells whose
    // cell_workflow_mode is "inherit_from_mission" resolve to this.
    const missionCellWorkflow = workflowAlias(mission && mission.cell_workflow);
    const workflowTemplates = (templates && templates.workflows) || [];
    const workflowOptions = [
      NONE_OPTION,
      ...(Array.isArray(workflowTemplates)
        ? workflowTemplates.map(w => w.alias).filter(Boolean).map(a => ({ value: a, label: a }))
        : []),
    ];
    const workflowSelectValue = workflowOptions.find(o => o.value === missionCellWorkflow) || NONE_OPTION;
    // The chosen workflow's object (step graph), resolved by alias for the preview under the picker.
    const selectedWorkflow = (missionCellWorkflow && Array.isArray(workflowTemplates))
      ? (workflowTemplates.find(w => w.alias === missionCellWorkflow) || null)
      : null;

    // Status — per-cell-status counts, in lifecycle order, omitting empty buckets.
    const counts = {};
    (cells || []).forEach(c => {
      const status = (c.props && c.props.status) || 'draft';
      counts[status] = (counts[status] || 0) + 1;
    });
    const presentStatuses = STATUSES.filter(s => counts[s]);

    return (
      <>
        {onClose && (
          <span className="workflow-controls-pane-right">
            <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
          </span>
        )}

        {/* Mission — one section with Status / Details / Workspace subsections, mirroring the
            Playground panel's layout: an h6 header + SectionLabel sub-labels with gray-chip tables. */}
        <Card.Text as="h6">Mission</Card.Text>
        <hr />

        {/* Mission ID + per-cell-status counts — one gray-chip properties table (value cells carry the
            .prop-value-column background). No separate Status subsection. */}
        <Card.Text>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">Mission ID</td>
                <td className="prop-value-column"><code>{mission ? mission.id : ''}</code></td>
              </tr>
              {/* The mission's OWN status, mirroring the cell panel's ID-then-Status pair. It is not
                  cosmetic: the orchestrator never re-reads an `archived` mission, so a cell added to
                  one is never picked up and nothing else on this screen says why. */}
              <tr>
                <td className="prop-name-column">Status</td>
                <td className="prop-value-column">
                  {mission ? <StatusPill status={mission.status} /> : null}
                </td>
              </tr>
              {presentStatuses.map(s => (
                <tr key={s}>
                  <td className="prop-name-column"><StatusPill status={s} /></td>
                  <td className="prop-value-column">{counts[s]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card.Text>

        {/* Details — editable Title + Description (a mission has no draft gate, so the only thing
            that makes these read-only is the revision or the session; saved on blur via
            client.setMissionProps, then reflected locally via onApplyLocal(upsertMission)).
            TITLE and DESCRIPTION are what these fields are called on every surface that touches them —
            this panel, the cell panel, both creation modals and the orchestrator. */}
        <SectionLabel>Details</SectionLabel>
        {!canEdit ? (
          // The same two lines the cell panel falls back to, through the same view — a mission's
          // title and description are the same pair of fields, and two renderings of them is how
          // the two panels come to disagree about what read-only prose looks like.
          <DetailsView title={(mission && mission.title) || missionId || ''}
            description={(mission && mission.description) || ''} />
        ) : (
        <>
        <Form.Group>
          <GrowTextarea
            rows={1}
            maxRows={TITLE_ROWS}
            value={titleDraft}
            placeholder="Mission title…"
            disabled={!mission}
            onChange={value => this.setState({ titleDraft: value })}
            onBlur={() => this._saveTitle()}
          />
          <Form.Text muted>What this mission is called.</Form.Text>
        </Form.Group>
        <Form.Group>
          <GrowTextarea
            rows={2}
            maxRows={DESCRIPTION_ROWS}
            value={descriptionDraft}
            placeholder="Mission description…"
            disabled={!mission}
            onChange={value => this.setState({ descriptionDraft: value })}
            onBlur={() => this._saveDescription()}
          />
          <Form.Text muted>What this mission is about.</Form.Text>
        </Form.Group>
        </>
        )}
        <SectionLabel>Cell Workflow</SectionLabel>
        {canEdit ? (
          <Form.Group>
            <Select
              options={workflowOptions}
              value={workflowSelectValue}
              onChange={opt => this._saveCellWorkflow(opt ? opt.value : '')}
              menuPortalTarget={document.body}
              placeholder="— none —"
              isDisabled={!mission}
            />
            {/* Preview the chosen workflow's step graph right under the picker. */}
            {selectedWorkflow && <WorkflowGraph workflow={selectedWorkflow} />}
            <Form.Text muted>Default steps for this mission's cells.</Form.Text>
          </Form.Group>
        ) : (
          // No picker, so no help text about choosing: what is left to say is what it IS. The name
          // and its step graph are exactly what the cell panel shows for a frozen workflow.
          <>
            {selectedWorkflow ? (
              <>
                <table className="properties">
                  <tbody>
                    <tr>
                      <td className="prop-name-column">Workflow</td>
                      <td className="prop-value-column"><code>{selectedWorkflow.alias}</code></td>
                    </tr>
                  </tbody>
                </table>
                <WorkflowGraph workflow={selectedWorkflow} />
              </>
            ) : <p className="mb-0">None</p>}
          </>
        )}
        <SectionLabel>Workspace</SectionLabel>
        {canEdit ? (
          <Form.Group>
            <Select
              options={workspaceOptions}
              value={workspaceSelectValue}
              onChange={opt => this._saveWorkspace(opt ? opt.value : '')}
              menuPortalTarget={document.body}
              placeholder="— none —"
              isDisabled={!mission}
            />
            <Form.Text muted>Default place its agents work.</Form.Text>
          </Form.Group>
        ) : (
          // Off the ENTRY, not off the select's value: that one falls back to NONE_OPTION rather
          // than to null, so a mission with no workspace would have read "— none —" — a picker's
          // empty-choice placeholder presented as a fact.
          <p className="mb-0">{selectedWorkspaceEntry ? selectedWorkspaceEntry.alias : 'None'}</p>
        )}
      </>
    );
  }
}

export default MissionPanel;
